import { formatDuration } from '@/lib/utils';

interface ExpandedMetadataRowProps {
  colSpan: number;
  isExpanded: boolean;
  metadata: {
    source_timecode?: string | null;
    duration_frames?: number | null;
    fps?: number | null;
    source_file_path?: string | null;
    tape_name?: string | null;
    custom_metadata?: Record<string, any> | null;
  };
  additionalFields?: { label: string; value: any }[];
}

/**
 * Expandable metadata row that shows additional details in a grid
 * Used in both Clips and VFX shots tables
 */
export function ExpandedMetadataRow({
  colSpan,
  isExpanded,
  metadata,
  additionalFields = []
}: ExpandedMetadataRowProps) {
  if (!isExpanded) return null;

  // Filter out core fields from custom metadata that are already displayed
  const coreFieldsToFilter = ['start', 'source in', 'source_in', 'source timecode', 'source_timecode', 'tc in', 'tc_in', 'end', 'tape', 'tape name', 'tapename', 'reel', 'description']
  const filteredCustomMetadata = metadata.custom_metadata
    ? Object.entries(metadata.custom_metadata).filter(([key]) =>
        !coreFieldsToFilter.includes(key.toLowerCase().trim())
      )
    : []

  return (
    <tr className="border-b">
      <td colSpan={colSpan} className="px-4 py-3 bg-muted/20">
        <div className="ml-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
          {/* Priority fields: Start, End, Duration, Tape */}
          {metadata.source_timecode && (
            <div className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase">Start</p>
              <p className="font-medium font-mono bg-muted/30 p-2 rounded">{metadata.source_timecode}</p>
            </div>
          )}
          {metadata.custom_metadata?.['End'] && (
            <div className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase">End</p>
              <p className="font-medium font-mono bg-muted/30 p-2 rounded">{String(metadata.custom_metadata['End'])}</p>
            </div>
          )}
          {metadata.duration_frames !== null && metadata.duration_frames !== undefined && (
            <div className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase">Duration</p>
              <p className="font-medium font-mono bg-muted/30 p-2 rounded">
                {formatDuration(metadata.duration_frames, metadata.fps ?? undefined)}
              </p>
            </div>
          )}
          {metadata.tape_name && (
            <div className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase">Tape Name</p>
              <p className="font-medium bg-muted/30 p-2 rounded">{metadata.tape_name}</p>
            </div>
          )}
          {/* Other core fields */}
          {metadata.fps && (
            <div className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase">FPS</p>
              <p className="font-medium bg-muted/30 p-2 rounded">{metadata.fps}</p>
            </div>
          )}
          {metadata.source_file_path && (
            <div className="space-y-1 col-span-2 md:col-span-3 lg:col-span-4">
              <p className="text-muted-foreground font-medium uppercase">Source File Path</p>
              <p className="font-medium font-mono break-all bg-muted/30 p-2 rounded">
                {metadata.source_file_path}
              </p>
            </div>
          )}
          {/* Additional fields from parent component */}
          {additionalFields.map((field, idx) => (
            <div key={idx} className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase">{field.label}</p>
              <p className="font-medium bg-muted/30 p-2 rounded break-words">{String(field.value)}</p>
            </div>
          ))}
          {/* Custom metadata - filtered */}
          {filteredCustomMetadata.map(([key, value]) => (
            <div key={key} className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase">{key}</p>
              <p className="font-medium bg-muted/30 p-2 rounded break-words">{String(value)}</p>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}
